import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import PageShell from '@/components/PageShell';
import SectionHeader from '@/components/SectionHeader';
import StatTicker from '@/components/StatTicker';

const heroImg =
  'https://images.unsplash.com/photo-1632040804304-4094a1a63be7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBoaWdod2F5JTIwbmlnaHR8ZW58MHx8fHwxNzgxNzkyMTY2fDA&ixlib=rb-4.1.0&q=85';

const About = () => {
  return (
    <PageShell>
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="indian highway" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-ink-900/85 to-ink-900" />
        </div>
        <div className="relative px-6 md:px-12 lg:px-16 pt-24 pb-20">
          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-px w-6 bg-amber inline-block" />
              About
            </span>
          </div>
          <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl text-balance max-w-5xl leading-[1.02]">
            A technology, consulting &amp; investment group — run by automotive operators.
          </h1>
          <p className="mt-8 max-w-3xl text-zinc-300 text-base md:text-lg leading-relaxed">
            The Mobility Group is built by founders and partners who have spent
            their careers inside the automotive industry — running OEM,
            dealership, data and technology businesses. We bring expertise,
            agility, AI &amp; analytics, and patient capital into a single
            partnership model — across platforms, solutions and ventures.
          </p>
        </div>
      </section>

      <StatTicker />

      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeader
              kicker="How we think"
              title="Sector-first. Operators-first. Data-first."
            />
          </div>
          <div className="lg:col-span-7 space-y-8">
            {[
              {
                t: 'Sector-first',
                d: 'We don\'t spread thin across industries. Everything we build, ship or fund is in automotive and mobility — and that focus compounds every year.',
              },
              {
                t: 'Operators-first',
                d: 'We run our own platforms at national scale. We know what an OEM dispatch hiccup, a dealer SLA breach, or a stuck insurance claim actually feels like.',
              },
              {
                t: 'Data-first',
                d: 'Across 397M+ vehicles and 1,500+ RTOs, our advantage is decades of lived data — the kind you can\'t buy off a data provider.',
              },
              {
                t: 'Honest & easy to work with',
                d: 'We name what we don\'t know. We don\'t promise what we can\'t ship. We\'re long-term partners — not consultants on the clock.',
              },
            ].map((b, i) => (
              <div key={b.t} className="border-l border-amber/30 pl-6 py-2">
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber mb-2">
                  / 0{i + 1}
                </div>
                <h3 className="font-display text-2xl mb-2">{b.t}</h3>
                <p className="text-zinc-400 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-black border-y border-white/10">
        <SectionHeader
          kicker="Reach"
          title="India-deep. Selectively global."
          intro="We start where we already operate — India's automotive ecosystem — and selectively take the most exportable parts of our stack to other markets."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            ['India core', 'OEMs, dealer networks, insurers, lenders, workshops, RTOs — covered at scale.'],
            ['Emerging markets', 'Adapting commercial vehicle, used-vehicle and dealer-tech playbooks for similar markets.'],
            ['Cross-border data', 'Industry data exchanges and standards we believe will travel globally.'],
          ].map(([t, d]) => (
            <div key={t} className="bg-ink-900 border border-white/10 p-8 hover:border-amber/40 transition-colors">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan mb-4">
                {t}
              </div>
              <p className="text-zinc-300 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-ink-900">
        <div className="max-w-3xl">
          <div className="overline text-amber mb-4">A note from the group</div>
          <p className="font-display text-2xl md:text-3xl leading-[1.25] text-balance text-white">
            "The automotive industry doesn't need more consultants with
            templates. It needs operators with data, conviction, and patience.
            That's what we've spent the last decade becoming — and what
            themobilitygroup.co is built to be."
          </p>
          <div className="mt-8 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
            The Mobility Group
          </div>

          <div className="mt-12">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
            >
              Get in touch
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default About;
